"use client";

import { useUserLGSs } from "./useUserLGSs";
import { useMemo } from "react";

/**
 * Hook for generating consistent user store navigation links
 * Handles loading states and store count variations
 *
 * @param userId - The user ID to fetch stores for
 * @returns Navigation helpers and store data
 *
 * @example
 * ```tsx
 * const { primaryStoreHref, hasStores, isLoading } = useUserStoreNavigation(userId);
 *
 * if (isLoading) return <Skeleton />;
 *
 * return <Link href={primaryStoreHref}>Visit Store</Link>
 * ```
 */
export function useUserStoreNavigation(userId: string) {
  const { userLgs, isInitialLoading } = useUserLGSs(userId);

  // Calculate primary store href based on store count
  const primaryStoreHref = useMemo(() => {
    if (!userLgs || userLgs.length === 0) {
      return "/x/stores";
    }

    if (userLgs.length === 1) {
      return `/x/stores/${userLgs[0].slug}`;
    }

    // Multiple stores - link to stores overview page
    return "/x/stores";
  }, [userLgs]);

  const hasStores = userLgs && userLgs.length > 0;
  const storeCount = userLgs?.length || 0;
  // Not `|| isFetching` — a background refetch of the same user's stores must
  // not send consumers back to their skeleton.
  const isLoading = isInitialLoading;

  return {
    /**
     * The primary navigation link for user's stores
     * - No stores: "/trade/stores/personal" (add stores page)
     * - 1 store: "/trade/stores/personal/slug" (direct to store)
     * - Multiple stores: "/trade/stores/personal" (stores overview)
     */
    primaryStoreHref,

    /**
     * Whether the user has any stores configured
     */
    hasStores,

    /**
     * Number of stores the user has
     */
    storeCount,

    /**
     * Array of user's stores (for building menus/dropdowns)
     */
    userLgs,

    /**
     * Loading state (initial load or refetching)
     */
    isLoading,

    /**
     * Initial loading state only
     */
    isInitialLoading,
  };
}
